import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  saveTranscript,
  TranscriptTurn,
  TranscriptDocument,
  ScenarioInfo,
} from "../services/api";

interface SaveTranscriptParams {
  sessionId: string;
  turns: TranscriptTurn[];
  scenario?: ScenarioInfo;
}

const useSaveTranscript = (options?: { enableOptimisticUpdates?: boolean }) => {
  const queryClient = useQueryClient();
  const enableOptimistic = options?.enableOptimisticUpdates ?? false;

  const mutation = useMutation({
    mutationKey: ["saveTranscript"],
    mutationFn: ({ sessionId, turns, scenario }: SaveTranscriptParams) =>
      saveTranscript(sessionId, turns, scenario),
    onMutate: enableOptimistic
      ? async ({ sessionId, turns, scenario }) => {
          // Cancel outgoing refetches
          await queryClient.cancelQueries({
            queryKey: ["transcript", sessionId],
          });

          // Snapshot previous value
          const previousTranscript =
            queryClient.getQueryData<TranscriptDocument>([
              "transcript",
              sessionId,
            ]);

          // Optimistically update cache
          queryClient.setQueryData<TranscriptDocument>(
            ["transcript", sessionId],
            {
              sessionId,
              scenario,
              turns,
              createdAt:
                previousTranscript?.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString(),
            }
          );

          return { previousTranscript };
        }
      : undefined,
    onError: enableOptimistic
      ? (err, variables, context) => {
          // Rollback on error
          if (context?.previousTranscript) {
            queryClient.setQueryData(
              ["transcript", variables.sessionId],
              context.previousTranscript
            );
          }
          console.error("Failed to save transcript:", err);
        }
      : undefined,
    onSettled: enableOptimistic
      ? (_data, _error, variables) => {
          // Refetch to ensure consistency
          queryClient.invalidateQueries({
            queryKey: ["transcript", variables.sessionId],
          });
        }
      : undefined,
  });
  return { ...mutation };
};

export default useSaveTranscript;